import { useEffect, useRef, useState } from "react";
import { Building2, Check, ChevronDown, Shield } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";

export default function CompanySwitcher() {
  const { user, switchCompany } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user) return null;
  const companies = user.companies || [];
  const isSuperAdmin = !!user.is_super_admin;

  // Only show the switcher when there's an actual choice OR the user is super-admin.
  if (companies.length <= 1 && !isSuperAdmin) {
    return (
      <div
        className={`mt-4 px-3 py-2 rounded-md ${isSuperAdmin ? "bg-amber-50 border border-amber-300 text-amber-900" : "bg-zinc-900 text-white"}`}
        data-testid="sidebar-company-badge"
      >
        <div className="cs-label !text-[9px] !text-white/50">tenant atual</div>
        <div className="text-xs font-semibold truncate mt-0.5" title={user.company_name}>
          {user.company_name || "—"}
        </div>
      </div>
    );
  }

  const onPick = async (cid) => {
    if (pending || cid === user.active_company_id) { setOpen(false); return; }
    setPending(cid);
    try {
      const next = await switchCompany(cid);
      setOpen(false);
      toast.success(`Empresa ativa: ${next.company_name}`);
      // Force a reload so all in-flight data refetches under the new tenant.
      setTimeout(() => window.location.reload(), 200);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setPending(null); }
  };

  return (
    <div className="relative mt-4" ref={ref} data-testid="company-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`w-full px-3 py-2 rounded-md flex items-start gap-2 transition-colors ${isSuperAdmin ? "bg-amber-50 border border-amber-300 text-amber-900 hover:bg-amber-100" : "bg-zinc-900 text-white hover:bg-zinc-800"}`}
        data-testid="company-switcher-trigger"
      >
        <div className="flex-1 min-w-0 text-left">
          <div className={`cs-label !text-[9px] ${isSuperAdmin ? "!text-amber-700" : "!text-white/50"}`}>
            {isSuperAdmin ? t("super_admin_multi") : t("tenant_current")}
          </div>
          <div className="text-xs font-semibold truncate mt-0.5" title={user.company_name}>
            {user.company_name || "—"}
          </div>
        </div>
        <ChevronDown size={14} className="mt-1 shrink-0 opacity-70" />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-zinc-200 rounded-md shadow-xl py-1 max-h-72 overflow-y-auto scrollbar-thin"
          data-testid="company-switcher-menu"
        >
          {isSuperAdmin && companies.length === 0 && (
            <div className="px-3 py-3 text-[11px] text-zinc-500">
              Como super-admin você vê todas as empresas. Selecione uma específica em "Configurações → Empresas" para focar.
            </div>
          )}
          {companies.map((c) => (
            <button
              key={c.id}
              onClick={() => onPick(c.id)}
              disabled={pending === c.id}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-zinc-50 disabled:opacity-60 ${c.is_active ? "bg-zinc-100" : ""}`}
              data-testid={`company-switcher-item-${c.id}`}
            >
              {c.role === "admin" ? <Shield size={12} className="text-cs-accent shrink-0" /> : <Building2 size={12} className="text-zinc-500 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-zinc-900 truncate">{c.name}</div>
                <div className="font-mono text-[10px] text-zinc-500 mt-0.5 uppercase">{c.role}</div>
              </div>
              {c.is_active && <Check size={12} className="text-cs-success shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
